import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { App } from "./App.js";
import { ApiError } from "./api/client.js";
import { consumeOAuthHash } from "./auth/token.js";
import { Toaster, toast } from "./components/toast.js";
import { TooltipProvider } from "./components/ui/tooltip.js";
import "./styles/index.css";

// Capture an OAuth-redirect token (#token=...) into localStorage before anything fetches.
consumeOAuthHash();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => {
        // Don't retry auth/permission failures.
        if (err instanceof ApiError && [401, 403, 404].includes(err.status))
          return false;
        return count < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
  // Global mutation error surfacing (§5.3).
  mutationCache: new MutationCache({
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : "Something went wrong";
      toast(message, "error");
    },
  }),
});

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
